import { useCallback, useEffect, useRef } from 'react';

import type { FormStepId } from 'shared/config/formStepId';
import { useAppDispatch, useAppSelector } from 'shared/lib';
import type { IInitialEvent } from 'shared/lib/sendGAEvents';

import type { TCreditFormCommonFields } from 'entities/creditForm';
import { isAutoValidationInProgressSelector } from 'entities/creditForm';

import { validateErrorHandlerThunk } from './validateErrorHandlerThunk';
import { validateSuccessHandlerThunk } from './validateSuccessHandlerThunk';
import type { TStepsValidateErrors, TValidateUserDataThunkPayload } from './validateUserDataThunk';
import { validateUserDataThunk } from './validateUserDataThunk';

export const useValidateData = (
  options: TValidateUserDataThunkPayload & {
    analyticsOverrides?: Partial<Pick<IInitialEvent, 'event' | 'eventCategory'>>;
    isFromSummary?: boolean;
  },
  enabledValidationOnMount = true,
  stepsToValidate?: Array<ValuesOf<typeof FormStepId>>,
) => {
  const dispatch = useAppDispatch();
  const isValidating = useAppSelector(isAutoValidationInProgressSelector);

  const optionsRef = useRef(options);
  const stepsToValidateRef = useRef(stepsToValidate);

  const validate = useCallback(
    (steps?: Array<ValuesOf<typeof FormStepId>>) => {
      const {
        onSuccess: onSuccessCallback,
        onError: onErrorCallback,
        onStepSuccess,
        analyticsOverrides,
        onComplete,
        isFromSummary,
      } = optionsRef.current;

      const onError = (errors: TStepsValidateErrors, validatedSteps: Array<ValuesOf<typeof FormStepId>>) => {
        dispatch(validateErrorHandlerThunk(errors, validatedSteps, isFromSummary, analyticsOverrides));
        onErrorCallback?.(errors, validatedSteps);
      };

      // todo передавать validatedSteps в validateSuccessHandlerThunk чтобы выводить события только по валидированным шагам
      const onSuccess = (formFields: TCreditFormCommonFields, lastValidatedStepId: ValuesOf<typeof FormStepId>) => {
        dispatch(validateSuccessHandlerThunk(analyticsOverrides));
        onSuccessCallback?.(formFields, lastValidatedStepId);
      };

      dispatch(
        validateUserDataThunk({
          onComplete,
          onSuccess,
          onError,
          stepsToValidate: steps || stepsToValidateRef.current,
          onStepSuccess,
        }),
      );
    },
    [dispatch],
  );

  useEffect(() => {
    optionsRef.current = options;
  }, [options]);

  useEffect(() => {
    stepsToValidateRef.current = stepsToValidate;
  }, [stepsToValidate]);

  useEffect(() => {
    if (enabledValidationOnMount) {
      validate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { isValidating, validate };
};
